/*
 * 戒导航 · 博客数据统计与排序（jiedh/blog-stats）
 * =============================================================================
 * 排序状态（模块级单例 + localStorage 持久化）
 *
 * 为什么用模块级状态而不是路由参数：
 *   fof/blog 的语言切换已经是「改内部 state → refreshParams」的模式；排序若走
 *   `m.route.set` 会重建页面组件（输入框失焦、列表闪烁、滚动位置丢失）。
 *   模块级状态还能被 BlogListState.requestParams() 直接读取，改动面最小。
 *
 * 数据流：
 *   BlogSortControl 点击 → setSort()（同时写 localStorage）→ 页面调用
 *   `list.refreshParams(listParams(), 1)` → BlogListState.requestParams() 里用
 *   `toParam(getSort())` 覆盖 fof/blog 硬编码的 `sort: '-createdAt'`。
 *
 * 可排序维度（正序 = 字段名，倒序 = `-字段名`）：
 *   createdAt    创建时间   —— flarum/core 内建
 *   lastPostedAt 修改时间   —— flarum/core 内建（口径 = 最后活动时间，见设计文档 §1.2）
 *   commentCount 评论数     —— flarum/core 内建
 *   viewCount    阅读次数   —— 本扩展（discussions.view_count）
 *   likeCount    点赞次数   —— 本扩展（discussions.like_count）
 */

/** 默认排序：创建时间倒序，与 fof/blog 原生行为完全一致（改造前用户无感） */
export const DEFAULT_BLOG_SORT = {
  key: 'createdAt',
  dir: 'desc'
};

/** localStorage 键名：带 jiedh- 前缀，避免与其它扩展/核心冲突 */
const STORAGE_KEY = 'jiedh-blog-sort';

/** 合法维度白名单：读取 localStorage 时过滤脏数据（旧版本键名、手改坏值等） */
const VALID_KEYS = ['createdAt', 'lastPostedAt', 'commentCount', 'viewCount', 'likeCount'];

/** localStorage 在当前环境是否可用（隐私模式 / SSR / 权限受限时会抛错） */
function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** 从 localStorage 读取排序；任何异常或非法值都回退默认值，保证首屏永远可渲染 */
export function loadSort() {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw) return {
      ...DEFAULT_BLOG_SORT
    };
    const parsed = JSON.parse(raw);
    const key = VALID_KEYS.indexOf(parsed.key) !== -1 ? parsed.key : DEFAULT_BLOG_SORT.key;
    const dir = parsed.dir === 'asc' ? 'asc' : 'desc';
    return {
      key,
      dir
    };
  } catch {
    return {
      ...DEFAULT_BLOG_SORT
    };
  }
}

/** 写回 localStorage（失败静默：排序持久化不是关键路径，不能因此打断用户操作） */
export function saveSort(sort) {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(sort));
  } catch {
    /* ignore */
  }
}

/** 模块级当前排序：页面首次构造 listParams / requestParams 时读取的就是它 */
let current = loadSort();
export function getSort() {
  return current;
}

/** 更新排序（同时持久化）。调用方负责触发列表刷新。 */
export function setSort(sort) {
  current = {
    key: sort.key,
    dir: sort.dir
  };
  saveSort(current);
}

/**
 * 转为 JSON:API 的 sort 参数：
 *   倒序 → `-viewCount`；正序 → `viewCount`（Flarum 统一约定）
 */
export function toParam(sort = current) {
  return sort.dir === 'desc' ? `-${sort.key}` : sort.key;
}